// ===========================================
// i18n CONFIGURATION
// ===========================================

import type { Language } from '@/types';

/**
 * i18n configuration
 */
export const i18nConfig = {
  // Default and fallback language
  defaultLanguage: 'en' as Language,
  fallbackLanguage: 'en' as Language,

  // Supported languages with metadata
  languages: {
    en: {
      code: 'en',
      name: 'English',
      nativeName: 'English',
      direction: 'ltr',
      locale: 'en-US',
      flag: '🇬🇧',
    },
    fr: {
      code: 'fr',
      name: 'French',
      nativeName: 'Français',
      direction: 'ltr',
      locale: 'fr-FR',
      flag: '🇫🇷',
    },
    ar: {
      code: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      direction: 'rtl',
      locale: 'ar-TN',
      flag: '🇹🇳',
    },
    de: {
      code: 'de',
      name: 'German',
      nativeName: 'Deutsch',
      direction: 'ltr',
      locale: 'de-DE',
      flag: '🇩🇪',
    },
    it: {
      code: 'it',
      name: 'Italian',
      nativeName: 'Italiano',
      direction: 'ltr',
      locale: 'it-IT',
      flag: '🇮🇹',
    },
    zh: {
      code: 'zh',
      name: 'Chinese',
      nativeName: '中文',
      direction: 'ltr',
      locale: 'zh-CN',
      flag: '🇨🇳',
    },
    es: {
      code: 'es',
      name: 'Spanish',
      nativeName: 'Español',
      direction: 'ltr',
      locale: 'es-ES',
      flag: '🇪🇸',
    },
  } as const,

  // Translation namespaces
  namespaces: [
    'common',
    'home',
    'homepage',
    'packages',
    'destinations',
    'excursions',
    'blog',
    'auth',
    'account',
    'admin', // Admin Dashboard static translations
    'errors',
    'validation',
    'booking',
    'about',
  ] as const,

  // Default namespace
  defaultNamespace: 'common',

  // Detection options
  detection: {
    // Order of detection
    order: ['path', 'cookie', 'localStorage', 'navigator'],
    // Cache user language
    caches: ['cookie', 'localStorage'],
    // Cookie options
    cookieName: 'i18next',
    cookieMinutes: 60 * 24 * 365, // 1 year
    // Path detection
    lookupFromPathIndex: 0,
  },

  // Interpolation options
  interpolation: {
    escapeValue: false, // React already escapes
  },

  // React options
  react: {
    useSuspense: true,
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
  },
} as const;

type LanguageKey = keyof typeof i18nConfig.languages;

/**
 * Get language direction
 */
export const getLanguageDirection = (lang: Language): 'ltr' | 'rtl' => {
  const config = i18nConfig.languages[lang as LanguageKey];
  return config?.direction || 'ltr';
};

/**
 * Get language metadata
 */
export const getLanguageInfo = (lang: Language) => {
  return i18nConfig.languages[lang as LanguageKey];
};

/**
 * Get all language options for dropdown
 */
export const getLanguageOptions = () => {
  return Object.values(i18nConfig.languages).map((lang) => ({
    value: lang.code,
    label: lang.nativeName,
    flag: lang.flag,
  }));
};

/**
 * Check if language is RTL
 */
export const isRTL = (lang: Language): boolean => {
  const config = i18nConfig.languages[lang as LanguageKey];
  return config?.direction === 'rtl';
};

export type I18nConfig = typeof i18nConfig;
export type Namespace = (typeof i18nConfig.namespaces)[number];
